import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  CalendarDaysIcon,
  LinkIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowTopRightOnSquareIcon,
  UserCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import {
  getIntegrationsStatus,
  getCalendlyAuthUrl,
  getGoogleCalendarAuthUrl,
  disconnectCalendly,
  disconnectGoogleCalendar,
  getTalentPool,
  createGoogleCalendarEvent,
  createCalendlyMeeting,
  summarizeMeeting
} from '../services/api';
import authService from '../services/auth';

function Integrations() {
  const location = useLocation();
  const [integrations, setIntegrations] = useState({
    calendly: { connected: false, schedulingUrl: null, email: null, name: null },
    googleCalendar: { connected: false, calendarId: null, email: null, calendarName: null }
  });
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Get current user's token to track user changes
  const currentUserToken = authService.getCurrentUser()?.token;

  // Meeting Notetaker Modal State
  const [showNotetaker, setShowNotetaker] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [meetingTitle, setMeetingTitle] = useState('');
  const [notetakerLoading, setNotetakerLoading] = useState(false);
  const [meetingNotes, setMeetingNotes] = useState(null);

  // Schedule Interview Modal State
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [scheduling, setScheduling] = useState(false);
  const [schedulingMethod, setSchedulingMethod] = useState(''); // 'google' or 'calendly'
  const [generatedLink, setGeneratedLink] = useState(null); // For Calendly generated links
  const [scheduleForm, setScheduleForm] = useState({
    candidateId: '',
    candidateName: '',
    candidateEmail: '',
    title: '',
    date: '',
    time: '',
    duration: '30',
    description: ''
  });

  useEffect(() => {
    // Reset state when user changes (extra safety measure)
    setIntegrations({
      calendly: { connected: false, schedulingUrl: null, email: null, name: null },
      googleCalendar: { connected: false, calendarId: null, email: null, calendarName: null }
    });
    setCurrentUser(null);

    // Check for OAuth callback success/error
    const params = new URLSearchParams(location.search);
    const success = params.get('success');
    const error = params.get('error');

    if (success === 'calendly_connected') {
      // Show success toast, but with verification note
      toast.success('Calendly connected! Please verify the correct account is shown below.', {
        autoClose: 5000
      });
      window.history.replaceState({}, '', '/integrations');
    } else if (success === 'google_connected') {
      toast.success('Google Calendar connected successfully!');
      window.history.replaceState({}, '', '/integrations');
    } else if (error) {
      const errorMessages = {
        'calendly_auth_failed': 'Failed to connect Calendly. Please try again.',
        'google_auth_failed': 'Failed to connect Google Calendar. Please try again.',
        'missing_params': 'Authentication failed. Missing parameters.',
        'invalid_state': 'Authentication failed. Invalid state.',
        'token_exchange_failed': 'Failed to complete authentication.',
        'calendly_callback_failed': 'Calendly connection failed. Please try again.',
        'google_callback_failed': 'Google Calendar connection failed. Please try again.',
        'calendly_already_connected': 'This Calendly account is already connected to another user. Please log out of Calendly at calendly.com and try again with YOUR Calendly account.',
        'google_already_connected': 'This Google Calendar is already connected to another user. Please use a different Google account.'
      };
      const isAlreadyConnected = error.includes('already_connected');
      toast.error(errorMessages[error] || 'Connection failed. Please try again.', {
        autoClose: isAlreadyConnected ? 10000 : 5000
      });
      window.history.replaceState({}, '', '/integrations');
    }

    loadIntegrationStatus();
    loadCandidates();
  }, [location, currentUserToken]); // Re-fetch when user token changes

  const loadIntegrationStatus = async () => {
    try {
      setLoading(true);
      const response = await getIntegrationsStatus();
      const data = response.data;

      if (data.success) {
        setIntegrations(data.integrations);
        setCurrentUser({ email: data.userEmail, id: data.userId });
        // Log for debugging
        console.log('Integration status loaded for user:', data.userEmail, data.userId);
        console.log('Calendly connected:', data.integrations.calendly.connected);
        console.log('Google connected:', data.integrations.googleCalendar.connected);
      }
    } catch (error) {
      console.error('Failed to load integration status:', error);
      // Check if it's an auth error
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else {
        toast.error('Failed to load integration status');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadCandidates = async () => {
    try {
      const response = await getTalentPool();
      if (response.data) {
        setCandidates(response.data.candidates || response.data || []);
      }
    } catch (error) {
      console.error('Failed to load candidates:', error);
    }
  };

  const handleConnectCalendly = async () => {
    // Show confirmation dialog with instructions about Calendly account switching
    const confirmed = window.confirm(
      'Connect to Calendly\n\n' +
      'You will be redirected to Calendly to authorize access.\n\n' +
      'IMPORTANT: If you want to connect a DIFFERENT Calendly account:\n' +
      '1. First go to calendly.com and sign out\n' +
      '2. Then come back here and click Connect\n\n' +
      'Otherwise, your currently logged-in Calendly account will be connected.\n\n' +
      'Continue?'
    );

    if (!confirmed) {
      return;
    }

    try {
      setConnecting('calendly');
      console.log('Connecting Calendly for user:', currentUser?.email);
      const response = await getCalendlyAuthUrl('/integrations');
      if (response.data.success && response.data.authUrl) {
        console.log('Got Calendly auth URL for userId:', response.data.userId);
        // Verify the auth URL is for the current user
        if (currentUser && response.data.userId && response.data.userId !== currentUser.id) {
          console.warn('Auth URL userId mismatch! Expected:', currentUser.id, 'Got:', response.data.userId);
          toast.error('Session mismatch. Please refresh the page and try again.');
          setConnecting(null);
          return;
        }
        window.location.href = response.data.authUrl;
      } else {
        toast.error('Failed to get Calendly auth URL');
        setConnecting(null);
      }
    } catch (error) {
      console.error('Calendly connect error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else {
        toast.error('Failed to connect Calendly');
      }
      setConnecting(null);
    }
  };

  const handleDisconnectCalendly = async () => {
    if (!window.confirm('Are you sure you want to disconnect Calendly?')) return;

    try {
      setConnecting('calendly');
      const response = await disconnectCalendly();
      if (response.data.success) {
        toast.success('Calendly disconnected');
        setIntegrations(prev => ({
          ...prev,
          calendly: { connected: false, schedulingUrl: null, email: null, name: null }
        }));
      }
    } catch (error) {
      console.error('Calendly disconnect error:', error);
      toast.error('Failed to disconnect Calendly');
    } finally {
      setConnecting(null);
    }
  };

  const handleConnectGoogle = async () => {
    try {
      setConnecting('google');
      console.log('Connecting Google Calendar for user:', currentUser?.email);
      const response = await getGoogleCalendarAuthUrl('/integrations');
      if (response.data.success && response.data.authUrl) {
        console.log('Got Google auth URL for userId:', response.data.userId);
        // Verify the auth URL is for the current user
        if (currentUser && response.data.userId && response.data.userId !== currentUser.id) {
          console.warn('Auth URL userId mismatch! Expected:', currentUser.id, 'Got:', response.data.userId);
          toast.error('Session mismatch. Please refresh the page and try again.');
          setConnecting(null);
          return;
        }
        window.location.href = response.data.authUrl;
      } else {
        toast.error('Failed to get Google auth URL');
        setConnecting(null);
      }
    } catch (error) {
      console.error('Google connect error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else {
        toast.error('Failed to connect Google Calendar');
      }
      setConnecting(null);
    }
  };

  const handleDisconnectGoogle = async () => {
    if (!window.confirm('Are you sure you want to disconnect Google Calendar?')) return;

    try {
      setConnecting('google');
      const response = await disconnectGoogleCalendar();
      if (response.data.success) {
        toast.success('Google Calendar disconnected');
        setIntegrations(prev => ({
          ...prev,
          googleCalendar: { connected: false, calendarId: null, email: null, calendarName: null }
        }));
      }
    } catch (error) {
      console.error('Google disconnect error:', error);
      toast.error('Failed to disconnect Google Calendar');
    } finally {
      setConnecting(null);
    }
  };

  // Helper: determine which scheduling method is active for both UI and logic
  const getActiveMethod = () => {
    if (schedulingMethod) return schedulingMethod;
    if (integrations.googleCalendar?.connected && !integrations.calendly?.connected) return 'google';
    if (!integrations.googleCalendar?.connected && integrations.calendly?.connected) return 'calendly';
    // Both connected but no explicit selection — default to google
    if (integrations.googleCalendar?.connected && integrations.calendly?.connected) return 'google';
    return 'google';
  };

  const handleScheduleInterview = async () => {
    const method = getActiveMethod();

    if (method === 'google') {
      // Google Calendar: requires email, date, time
      const missing = [];
      if (!scheduleForm.candidateEmail) missing.push('Candidate Email');
      if (!scheduleForm.date) missing.push('Date');
      if (!scheduleForm.time) missing.push('Time');
      if (missing.length > 0) {
        toast.error(`Please fill in: ${missing.join(', ')}`);
        return;
      }

      try {
        setScheduling(true);

        // Calculate end time based on duration
        const startDateTime = new Date(`${scheduleForm.date}T${scheduleForm.time}`);
        const endDateTime = new Date(startDateTime.getTime() + parseInt(scheduleForm.duration) * 60000);

        const eventData = {
          title: scheduleForm.title || `Interview with ${scheduleForm.candidateName || 'Candidate'}`,
          description: scheduleForm.description || 'Interview scheduled via AutoHire',
          startTime: startDateTime.toISOString(),
          endTime: endDateTime.toISOString(),
          attendees: [scheduleForm.candidateEmail],
          addMeet: true,
          sendInvites: true
        };

        const response = await createGoogleCalendarEvent(eventData);

        if (response.data.success) {
          toast.success('Interview scheduled! Calendar invite sent to candidate.');
          closeScheduleModal();
        } else {
          toast.error(response.data.error || 'Failed to schedule interview');
        }
      } catch (error) {
        console.error('Schedule interview error:', error);
        toast.error('Failed to schedule interview. Make sure Google Calendar is connected.');
      } finally {
        setScheduling(false);
      }
    } else {
      // Calendly: create one-off meeting link
      try {
        setScheduling(true);

        const response = await createCalendlyMeeting({
          candidateName: scheduleForm.candidateName || 'Candidate',
          candidateEmail: scheduleForm.candidateEmail,
          title: scheduleForm.title,
          duration: scheduleForm.duration
        });

        if (response.data.success) {
          setGeneratedLink({
            url: response.data.meetingLink,
            eventType: response.data.eventType,
            candidateName: scheduleForm.candidateName || 'Candidate'
          });
          toast.success('Meeting link created! Copy and share with the candidate.');
        } else {
          toast.error(response.data.error || 'Failed to create meeting link');
        }
      } catch (error) {
        console.error('Calendly meeting error:', error);
        toast.error(error.response?.data?.error || 'Failed to create Calendly meeting link');
      } finally {
        setScheduling(false);
      }
    }
  };

  const closeScheduleModal = () => {
    setShowScheduleModal(false);
    setGeneratedLink(null);
    setSchedulingMethod('');
    setScheduleForm({
      candidateId: '',
      candidateName: '',
      candidateEmail: '',
      title: '',
      date: '',
      time: '',
      duration: '30',
      description: ''
    });
  };

  const handleCandidateSelect = (e) => {
    const candidateId = e.target.value;
    const candidate = candidates.find(c => c.id === candidateId);
    if (candidate) {
      setScheduleForm(prev => ({
        ...prev,
        candidateId: candidate.id,
        candidateName: candidate.fullName || candidate.name || '',
        candidateEmail: candidate.email || prev.candidateEmail || '',
        title: `Interview with ${candidate.fullName || candidate.name || 'Candidate'}`
      }));
    }
  };

  const handleSummarizeMeeting = async () => {
    if (!transcript.trim()) {
      toast.error('Paste a meeting transcript first');
      return;
    }
    setNotetakerLoading(true);
    setMeetingNotes(null);
    try {
      const res = await summarizeMeeting({ transcript, meetingTitle });
      setMeetingNotes(res.data);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to summarize meeting');
    } finally {
      setNotetakerLoading(false);
    }
  };

  const closeNotetaker = () => {
    setShowNotetaker(false);
    setTranscript('');
    setMeetingTitle('');
    setMeetingNotes(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Integrations</h1>
        <p className="mt-2 text-gray-600">
          Connect your calendar apps to schedule interviews with candidates.
        </p>
        {currentUser && (
          <p className="mt-1 text-sm text-indigo-600">
            Logged in as: {currentUser.email}
          </p>
        )}
      </div>

      {/* Schedule Interview Button */}
      <div className="mb-6">
        <div className="flex items-center space-x-3">
          {/* Schedule Interview - works with either Google Calendar or Calendly */}
          <button
            onClick={() => setShowScheduleModal(true)}
            disabled={!integrations.googleCalendar?.connected && !integrations.calendly?.connected}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ClockIcon className="h-5 w-5 mr-2" />
            Schedule Interview
          </button>

          {/* Quick copy Calendly link */}
          {integrations.calendly?.connected && integrations.calendly?.schedulingUrl && (
            <button
              onClick={() => {
                navigator.clipboard.writeText(integrations.calendly.schedulingUrl);
                toast.success('Calendly link copied! Share with candidate to let them book.');
              }}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
            >
              <LinkIcon className="h-5 w-5 mr-2" />
              Copy Calendly Link
            </button>
          )}
        </div>

        {/* Help text based on connection status */}
        {!integrations.googleCalendar?.connected && !integrations.calendly?.connected && (
          <p className="text-sm text-gray-500 mt-2">
            Connect Google Calendar or Calendly to schedule interviews with candidates.
          </p>
        )}
      </div>

      <div className="space-y-6">
        {/* Calendly Integration */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <CalendarDaysIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Calendly</h3>
                  <p className="text-sm text-gray-500">
                    Let candidates book interviews directly from your scheduling page.
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {integrations.calendly?.connected ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircleIcon className="w-4 h-4 mr-1" />
                    Connected
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    <XCircleIcon className="w-4 h-4 mr-1" />
                    Not Connected
                  </span>
                )}
              </div>
            </div>

            {integrations.calendly?.connected ? (
              <div className="mt-6 space-y-4">
                {/* Connected Account Info */}
                <div className="p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 text-sm text-blue-800">
                      <UserCircleIcon className="h-5 w-5 text-blue-600" />
                      <span>
                        <strong>Connected as:</strong> {integrations.calendly.name || integrations.calendly.email}
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        if (window.confirm(
                          'Wrong account connected?\n\n' +
                          'To connect a different Calendly account:\n' +
                          '1. Click "Disconnect" below\n' +
                          '2. Go to calendly.com and sign out\n' +
                          '3. Sign in to your desired Calendly account\n' +
                          '4. Come back here and click "Connect with Calendly"\n\n' +
                          'Click OK to disconnect now.'
                        )) {
                          handleDisconnectCalendly();
                        }
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800 underline"
                    >
                      Wrong account?
                    </button>
                  </div>
                </div>

                {/* Scheduling URL */}
                {integrations.calendly.schedulingUrl && (
                  <div className="flex items-center space-x-2 text-sm">
                    <LinkIcon className="h-4 w-4 text-gray-400" />
                    <a
                      href={integrations.calendly.schedulingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:text-indigo-800 flex items-center"
                    >
                      {integrations.calendly.schedulingUrl}
                      <ArrowTopRightOnSquareIcon className="h-3 w-3 ml-1" />
                    </a>
                  </div>
                )}

                <div className="flex space-x-3 pt-2">
                  <button
                    onClick={handleDisconnectCalendly}
                    disabled={connecting === 'calendly'}
                    className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
                  >
                    {connecting === 'calendly' ? 'Disconnecting...' : 'Disconnect'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-6">
                <button
                  onClick={handleConnectCalendly}
                  disabled={connecting === 'calendly'}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {connecting === 'calendly' ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Connecting...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z"/>
                      </svg>
                      Connect with Calendly
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Google Calendar Integration */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-white rounded-lg flex items-center justify-center border border-gray-200">
                  <svg className="h-6 w-6" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Google Calendar</h3>
                  <p className="text-sm text-gray-500">
                    Create interview events with Google Meet links automatically.
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {integrations.googleCalendar?.connected ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircleIcon className="w-4 h-4 mr-1" />
                    Connected
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    <XCircleIcon className="w-4 h-4 mr-1" />
                    Not Connected
                  </span>
                )}
              </div>
            </div>

            {integrations.googleCalendar?.connected ? (
              <div className="mt-6 space-y-4">
                {/* Connected Account Info */}
                <div className="p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center space-x-2 text-sm text-green-800">
                    <UserCircleIcon className="h-5 w-5 text-green-600" />
                    <span>
                      <strong>Connected as:</strong> {integrations.googleCalendar.email || integrations.googleCalendar.calendarName || 'Google Account'}
                    </span>
                  </div>
                </div>

                <p className="text-sm text-gray-600">
                  You can now schedule interviews and automatically create Google Meet links.
                </p>

                <div className="flex space-x-3 pt-2">
                  <button
                    onClick={handleDisconnectGoogle}
                    disabled={connecting === 'google'}
                    className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
                  >
                    {connecting === 'google' ? 'Disconnecting...' : 'Disconnect'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-6">
                <button
                  onClick={handleConnectGoogle}
                  disabled={connecting === 'google'}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  {connecting === 'google' ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Connecting...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Sign in with Google
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* How it Works Section */}
        <div className="bg-indigo-50 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-indigo-900 mb-3">How to Schedule Interviews</h3>
          <ul className="space-y-3 text-sm text-indigo-800">
            <li className="flex items-start">
              <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-indigo-200 text-indigo-700 text-xs font-medium mr-3">1</span>
              <span><strong>Connect Google Calendar:</strong> Sign in with your Google account to create events.</span>
            </li>
            <li className="flex items-start">
              <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-indigo-200 text-indigo-700 text-xs font-medium mr-3">2</span>
              <span><strong>Connect Calendly (optional):</strong> Let candidates self-schedule using your Calendly link.</span>
            </li>
            <li className="flex items-start">
              <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-indigo-200 text-indigo-700 text-xs font-medium mr-3">3</span>
              <span><strong>Schedule Interview:</strong> Click the button above to create a meeting with a candidate.</span>
            </li>
          </ul>
        </div>

        {/* Meeting Notetaker Integration */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(31,157,107,0.12)' }}>
                  <DocumentTextIcon className="h-6 w-6" style={{ color: '#2563EB' }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Meeting Notetaker</h3>
                  <p className="text-sm text-gray-500">
                    Paste an interview transcript and get a structured AI summary — key points, tags, and a hiring recommendation.
                  </p>
                </div>
              </div>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: 'rgba(31,157,107,0.12)', color: '#2563EB' }}>
                <SparklesIcon className="w-4 h-4 mr-1" />
                AI
              </span>
            </div>
            <div className="mt-6">
              <button
                onClick={() => setShowNotetaker(true)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90"
                style={{ backgroundColor: '#2563EB' }}
              >
                <DocumentTextIcon className="h-5 w-5 mr-2" />
                Summarize a Meeting
              </button>
              <p className="text-xs text-gray-500 mt-2">
                Tip: an automated bot that joins calls and transcribes them (e.g. Recall.ai) can feed this directly — coming soon.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Schedule Interview Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Schedule Interview</h2>

              {/* Show generated link if Calendly created one */}
              {generatedLink ? (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <h3 className="text-sm font-medium text-green-800 mb-2">
                      Meeting Link Created!
                    </h3>
                    <p className="text-xs text-green-700 mb-3">
                      Share this link with {generatedLink.candidateName} to let them book a {generatedLink.eventType?.duration}-minute {generatedLink.eventType?.name || 'meeting'}.
                    </p>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        readOnly
                        value={generatedLink.url}
                        className="flex-1 px-3 py-2 text-sm bg-white border border-green-300 rounded-lg"
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(generatedLink.url);
                          toast.success('Link copied to clipboard!');
                        }}
                        className="px-3 py-2 text-sm font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={closeScheduleModal}
                    className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Method Selection - only show if both are connected */}
                  {integrations.googleCalendar?.connected && integrations.calendly?.connected && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Scheduling Method
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => setSchedulingMethod('google')}
                          className={`p-3 text-sm rounded-lg border-2 transition-colors ${
                            (schedulingMethod === 'google' || (!schedulingMethod && integrations.googleCalendar?.connected))
                              ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="font-medium">Google Calendar</div>
                          <div className="text-xs text-gray-500 mt-1">Create event directly</div>
                        </button>
                        <button
                          onClick={() => setSchedulingMethod('calendly')}
                          className={`p-3 text-sm rounded-lg border-2 transition-colors ${
                            schedulingMethod === 'calendly'
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="font-medium">Calendly</div>
                          <div className="text-xs text-gray-500 mt-1">Generate booking link</div>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Candidate Selection */}
                  {candidates.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Select Candidate
                      </label>
                      <select
                        value={scheduleForm.candidateId}
                        onChange={handleCandidateSelect}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">-- Select a candidate --</option>
                        {candidates.map(candidate => (
                          <option key={candidate.id} value={candidate.id}>
                            {candidate.fullName || candidate.name} {candidate.email ? `- ${candidate.email}` : ''}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Or enter details manually below</p>
                    </div>
                  )}

                  {/* Candidate Name (for Calendly) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Candidate Name
                    </label>
                    <input
                      type="text"
                      value={scheduleForm.candidateName}
                      onChange={(e) => setScheduleForm(prev => ({ ...prev, candidateName: e.target.value }))}
                      placeholder="John Doe"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  {/* Candidate Email - required for Google, optional for Calendly */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Candidate Email {(getActiveMethod() === 'google') ? '*' : '(optional)'}
                    </label>
                    <input
                      type="email"
                      value={scheduleForm.candidateEmail}
                      onChange={(e) => setScheduleForm(prev => ({ ...prev, candidateEmail: e.target.value }))}
                      placeholder="candidate@email.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  {/* Duration - for Calendly matching */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duration
                    </label>
                    <select
                      value={scheduleForm.duration}
                      onChange={(e) => setScheduleForm(prev => ({ ...prev, duration: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="15">15 minutes</option>
                      <option value="30">30 minutes</option>
                      <option value="45">45 minutes</option>
                      <option value="60">1 hour</option>
                      <option value="90">1.5 hours</option>
                    </select>
                  </div>

                  {/* Date and Time - only for Google Calendar */}
                  {(getActiveMethod() === 'google') && (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date *
                          </label>
                          <input
                            type="date"
                            value={scheduleForm.date}
                            onChange={(e) => setScheduleForm(prev => ({ ...prev, date: e.target.value }))}
                            min={new Date().toISOString().split('T')[0]}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Time *
                          </label>
                          <input
                            type="time"
                            value={scheduleForm.time}
                            onChange={(e) => setScheduleForm(prev => ({ ...prev, time: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                      </div>

                      {/* Description - only for Google */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <textarea
                          value={scheduleForm.description}
                          onChange={(e) => setScheduleForm(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Interview notes or agenda..."
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                    </>
                  )}

                  {/* Info based on method */}
                  {(getActiveMethod() === 'google') ? (
                    <div className="p-3 bg-green-50 rounded-lg">
                      <p className="text-xs text-green-800">
                        A Google Meet link will be automatically created and sent to the candidate.
                      </p>
                    </div>
                  ) : (
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-xs text-blue-800">
                        A unique Calendly booking link will be generated. Share it with the candidate to let them pick a time.
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="mt-6 flex space-x-3">
                    <button
                      onClick={closeScheduleModal}
                      disabled={scheduling}
                      className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleScheduleInterview}
                      disabled={scheduling}
                      className="flex-1 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {scheduling ? 'Creating...' :
                        (getActiveMethod() === 'calendly')
                          ? 'Generate Link'
                          : 'Schedule Interview'
                      }
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Meeting Notetaker Modal */}
      {showNotetaker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Meeting Notetaker</h2>
                <button onClick={closeNotetaker} className="text-gray-400 hover:text-gray-600">
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meeting title (optional)</label>
                  <input
                    type="text"
                    value={meetingTitle}
                    onChange={(e) => setMeetingTitle(e.target.value)}
                    placeholder="e.g. Technical screen — Jane Doe"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': '#2563EB' }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Transcript</label>
                  <textarea
                    rows={8}
                    value={transcript}
                    onChange={(e) => setTranscript(e.target.value)}
                    placeholder="Paste the meeting or interview transcript here..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 text-sm"
                    style={{ '--tw-ring-color': '#2563EB' }}
                  />
                </div>

                {meetingNotes && (
                  <div className="p-4 rounded-lg border" style={{ backgroundColor: 'rgba(31,157,107,0.06)', borderColor: 'rgba(31,157,107,0.3)' }}>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Summary</h3>
                    <p className="text-sm text-gray-700 mb-3">{meetingNotes.summary}</p>
                    {meetingNotes.tags && meetingNotes.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {meetingNotes.tags.map((tag, i) => (
                          <span key={i} className="px-2 py-0.5 text-xs rounded-full" style={{ backgroundColor: 'rgba(31,157,107,0.12)', color: '#2563EB' }}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-4 text-xs text-gray-600">
                      {meetingNotes.recommendation && <span><strong>Recommendation:</strong> {meetingNotes.recommendation}</span>}
                      {meetingNotes.rating != null && <span><strong>Rating:</strong> {meetingNotes.rating}/5</span>}
                    </div>
                  </div>
                )}

                <div className="flex space-x-3 pt-2">
                  <button
                    onClick={closeNotetaker}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Close
                  </button>
                  <button
                    onClick={handleSummarizeMeeting}
                    disabled={notetakerLoading}
                    className="flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                    style={{ backgroundColor: '#2563EB' }}
                  >
                    {notetakerLoading ? 'Summarizing...' : 'Summarize'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Integrations;
